<!-- The glass. Live DOM text in the device's font, on one of three screen kinds:
     classic grey-green, two-way blue, or the TRACKBALL's colour screen. High
     contrast (Settings) swaps any of them to black on white via
     :root[data-lcd-contrast='high'] in device.css.

     THE BACKLIGHT. `glow-key` is anything that changes when a page arrives
     (the store's lastArrival id); each change lights the EL backlight for 4s.
     A key press calls the exposed `light()`. Under prefers-reduced-motion it is
     a plain on/off — no fade.

     Content is the default slot; the page items inside carry their own
     accessible labels. This component adds no live region — arrivals are
     announced once, by the store. -->
<template>
  <div
    class="pm-lcd"
    :class="[`pm-lcd--${kind}`, `pm-lcd--font-${font}`, { 'is-lit': lit, 'pm-lcd--compact': compact }]"
  >
    <div class="pm-lcd__glass">
      <slot />
    </div>
  </div>
</template>

<script>
// Pure helpers the device views share (exported from the SFC's plain script
// block so they stay beside the screen they describe).

import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { graphemes as splitGraphemes, isEmoji as emojiGrapheme } from '../pm/text.js';

const SEG_ONLY = /^[0-9 -]+$/;

/**
 * The body as a seven-segment string, or '' when it is not pure digits.
 * DSEG draws '!' as a digit-wide blank, so spaces become '!' and the ghost
 * segments behind stay aligned column for column.
 */
export function segForm(body) {
  const s = String(body ?? '').trim();
  if (!s || !SEG_ONLY.test(s) || !/\d/.test(s)) return '';
  return s.replace(/ /g, '!');
}

/** Digits plus emoji ("07734 👋"): seven-segment digits, pixel emoji, no ghost. */
export function isSegMixed(body) {
  const gs = splitGraphemes(String(body ?? '').trim());
  if (!gs.some((g) => /\d/.test(g))) return false;
  return gs.every((g) => /^[0-9 -]$/.test(g) || emojiGrapheme(g));
}

/**
 * THE MESSAGE WINDOW. A pager's screen is a fixed number of rows; a page longer
 * than that scrolls inside it — the glass never grows, so the keys never move.
 *
 * `el` is the window (a real scroller: overflow-y auto, scrollbar hidden,
 * overscroll contained); the full text stays in the DOM and only `scrollTop`
 * moves.
 *
 * Auto-scroll, like the real thing: show the first rows for 1.5s, then step up
 * one row every 1.2s (a jump, not a glide), hold the end for 2s, snap back to
 * the top and go round again — while `auto()` is true, the page is on screen and
 * the document is visible. `key()` changing (a new page, details on/off) starts
 * over from the top. Under reduced motion it never moves on its own.
 *
 * YOU CAN SCROLL IT. Drag (touch), wheel / trackpad and the arrow keys (when
 * the window has focus) scroll the text directly; a tap on the glass calls
 * `step()` — one row on, from the end back to the top. Any of these pauses the
 * auto-scroll; it picks up again from where you left it after 5s of quiet.
 * A hand-scroll settles on a whole row.
 *
 * `more` — rows hidden below the current position — drives the ▼ in the status
 * bar. `overflows` — the page is longer than the window at all.
 */
export function useLcdScroll({ el, key, auto = () => true, reduced = () => false }) {
  const more = ref(false);
  const overflows = ref(false);
  const FIRST_MS = 1500;
  const ROW_MS = 1200;
  const END_MS = 2000;
  const IDLE_MS = 5000;
  let timer = null;
  let ro = null;
  let bound = null;          // the window the listeners are on
  let oursUntil = 0;         // scroll events before this time are ours, not the user's
  let settleTimer = null;

  const win = () => el.value || null;
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  // A glyph (an emoji picture, a descender) can poke a pixel or two past the
  // last row: that is not "more text", so anything under a third of a row is
  // ignored.
  const maxTop = (w) => {
    const over = w.scrollHeight - w.clientHeight;
    return over > rowPx(w) / 3 ? over : 0;
  };

  function rowPx(w) {
    const cs = getComputedStyle(w);
    const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.2 || w.clientHeight;
    return lh + (parseFloat(cs.rowGap) || 0);
  }

  function measure() {
    const w = win();
    overflows.value = !!w && maxTop(w) > 1;
    more.value = !!w && maxTop(w) - w.scrollTop > 1;
    return w;
  }

  /** Move the window ourselves (so the scroll listener knows it wasn't you). */
  function setTop(w, top) {
    const t = Math.max(0, Math.min(maxTop(w), Math.round(top)));
    if (Math.abs(w.scrollTop - t) < 1) return;
    oursUntil = Date.now() + 120;
    w.scrollTop = t;
  }

  function clear() {
    if (timer) clearTimeout(timer);
    timer = null;
  }

  function canAuto() {
    return !!win() && auto() && !reduced() && !hidden();
  }

  function schedule(ms) {
    clear();
    if (!canAuto()) return;
    timer = setTimeout(tick, ms);
  }

  function tick() {
    timer = null;
    const w = measure();
    if (!w || !canAuto()) return;
    const max = maxTop(w);
    if (max <= 1) return;                          // it all fits: nothing to do
    if (w.scrollTop >= max - 1) {                  // held the end: back to the top
      setTop(w, 0);
      measure();
      schedule(FIRST_MS);
      return;
    }
    setTop(w, w.scrollTop + rowPx(w));
    measure();
    schedule(w.scrollTop >= max - 1 ? END_MS : ROW_MS);
  }

  /** Back to the top and start the cycle again. */
  function reset() {
    clear();
    const w = win();
    if (w) setTop(w, 0);
    measure();
    schedule(FIRST_MS);
  }

  /** The user did something: stop, and carry on from here after a quiet 5s. */
  function pause() {
    measure();
    schedule(IDLE_MS);
  }

  /** One row on (READ / a tap). False when the page fits and nothing moved. */
  function step() {
    const w = win();
    if (!w) return false;
    const max = maxTop(w);
    if (max <= 1) return false;
    setTop(w, w.scrollTop >= max - 1 ? 0 : w.scrollTop + rowPx(w));
    measure();
    schedule(IDLE_MS);
    return true;
  }

  /** Arrow keys on the focused window: a row at a time. */
  function onKey(e) {
    const w = win();
    if (!w || maxTop(w) <= 1) return;
    let d = 0;
    if (e.key === 'ArrowDown') d = 1;
    else if (e.key === 'ArrowUp') d = -1;
    else if (e.key === 'PageDown' || e.key === ' ') d = 3;
    else if (e.key === 'PageUp') d = -3;
    else if (e.key === 'Home') { e.preventDefault(); e.stopPropagation(); setTop(w, 0); pause(); return; }
    else if (e.key === 'End') { e.preventDefault(); e.stopPropagation(); setTop(w, maxTop(w)); pause(); return; }
    if (!d) return;
    e.preventDefault();
    e.stopPropagation();          // the screen's own arrow keys step pages
    setTop(w, w.scrollTop + d * rowPx(w));
    pause();
  }

  // A hand-scroll (drag, wheel) settles on a whole row once it stops.
  function settle() {
    settleTimer = null;
    const w = win();
    if (!w) return;
    const r = rowPx(w);
    const max = maxTop(w);
    const top = w.scrollTop >= max - 1 ? max : Math.round(w.scrollTop / r) * r;
    setTop(w, top);
    measure();
  }
  function onScroll() {
    if (Date.now() < oursUntil) { measure(); return; }
    measure();
    pause();
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(settle, 140);
  }
  const onUser = () => { if (overflows.value) pause(); };

  function bind(w) {
    if (bound === w) return;
    unbind();
    if (!w) return;
    bound = w;
    w.addEventListener('scroll', onScroll, { passive: true });
    w.addEventListener('wheel', onUser, { passive: true });
    w.addEventListener('touchstart', onUser, { passive: true });
    w.addEventListener('keydown', onKey);
  }
  function unbind() {
    if (!bound) return;
    bound.removeEventListener('scroll', onScroll);
    bound.removeEventListener('wheel', onUser);
    bound.removeEventListener('touchstart', onUser);
    bound.removeEventListener('keydown', onKey);
    bound = null;
  }

  const onVisibility = () => { if (hidden()) clear(); else schedule(FIRST_MS); };

  // A new page / window: start from the top once it has been laid out.
  watch([key, el], () => nextTick(() => {
    bind(win());
    reset();
    ro?.disconnect();
    const w = win();
    if (w && typeof ResizeObserver === 'function') {
      // Late layout (web fonts, emoji pictures) can make a page overflow after
      // the first look: start the cycle then if it is not already running.
      ro = new ResizeObserver(() => { measure(); if (more.value && !timer) schedule(FIRST_MS); });
      ro.observe(w);
      if (w.firstElementChild) ro.observe(w.firstElementChild);
    }
  }), { immediate: true });
  watch([auto, reduced], () => { if (canAuto()) schedule(FIRST_MS); else clear(); });

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibility);
    // Web fonts settle after mount and can change how many rows a page takes.
    document.fonts?.ready?.then(() => measure());
  });
  onBeforeUnmount(() => {
    clear();
    if (settleTimer) clearTimeout(settleTimer);
    unbind();
    ro?.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
  });

  return { more, overflows, step, reset, measure, pause };
}

/** "143 TO YOU TOO" + "I love you" → "143 = I LOVE YOU". */
export function hintLine(body, meaning) {
  if (!meaning) return '';
  const code = String(body ?? '').trim().split(/\s/)[0].replace(/[^\d-]+$/u, '');
  return `${code} = ${String(meaning).toUpperCase()}`;
}
</script>

<script setup>
// ref / watch / onBeforeUnmount come from the plain <script> block's import.
import { computed, provide } from 'vue';
import { settings } from '../store.js';

const props = defineProps({
  kind: { type: String, default: 'classic' },      // classic | blue | color
  font: { type: String, default: 'dot' },          // seg | dot | color
  glowKey: { type: [String, Number, Object], default: null },
  /** Start lit (a thumbnail of a device mid-page, the share image). */
  glow: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const GLOW_MS = 4000;
const lit = ref(props.glow);
let timer = null;

function light(ms = GLOW_MS) {
  lit.value = true;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { lit.value = props.glow; timer = null; }, ms);
}

watch(() => props.glowKey, (now, before) => {
  if (now != null && now !== before) light();
});
watch(() => props.glow, (on) => { if (!timer) lit.value = on; });
onBeforeUnmount(() => { if (timer) clearTimeout(timer); });

// Emoji on a monochrome screen take a 20% multiply of its glass. White glass
// (high contrast) and the colour screen take none.
const tint = computed(() => {
  if (settings?.highContrast) return '';
  if (props.kind === 'blue') return '--lcd-blue-glass';
  if (props.kind === 'color') return '';
  return '--lcd-glass';
});
provide('pmLcdTint', tint);

defineExpose({ light });
</script>
